import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "export-content__settings" }
const _hoisted_2 = { class: "export-content__setting export-content__notion" }
const _hoisted_3 = { class: "export-content__form--section" }
const _hoisted_4 = { class: "export-content__label-wrapper" }
const _hoisted_5 = { class: "export-content__label-wrapper" }
const _hoisted_6 = { class: "export-content__submit-buttons" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]

import { onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { Sentry } from '@/services/sentry'

import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import FormError from '@/components/ui/FormError.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import { ValidationState } from '@/helpers/form'

import DisablePopup from '@/app/components/export/DisablePopup.vue'
import { backend } from '@/services/backend'
import { parseDatabaseUrl } from '@/services/notion'


export default /*@__PURE__*/_defineComponent({
  __name: 'NotionSettings',
  props: {
    isNotionEnabled: { type: Boolean, default: false }
  },
  emits: ["updateEnabled"],
  setup(__props: any, { emit: __emit }) {

const validation = ref(new ValidationState())
const token = ref<string | null>(null)
const database = ref<string | null>(null)

const props = __props

const emit = __emit
const wait = useWait()

const popupDisable = ref<InstanceType<typeof DisablePopup> | null>(null)

onBeforeMount(async () => {
  await refresh()
  validation.value.reset()
})

async function refresh(): Promise<void> {
  const credentials = await backend.getNotionCredentials()
  token.value = credentials.notion_integration_token
  if (!credentials.notion_database_id) {
    database.value = null
  } else {
    database.value = `https://www.notion.so/${credentials.notion_database_id}`
  }
  emit('updateEnabled', token.value !== null && database.value !== null)
}

async function checkForm(): Promise<void> {
  if (props.isNotionEnabled) {
    disableNotion()
  } else {
    await enableNotion()
  }
}

function getErrorMessage(error: any): string {
  if (error.name === 'TypeError' && error.toString().includes('URL')) {
    return 'Invalid Notion Link'
  } else if (error.response === undefined) {
    Sentry.captureException(error)
    return 'Could not talk to backend. Network or authorisation error?'
  } else {
    const maybeErrorMessage = error?.response?.data?.errors?._schema?.[0]
    if (maybeErrorMessage === 'Insufficient permissions for this endpoint.') {
      return 'Make sure your integration has read and insert capabilities.'
    }
    Sentry.captureException(error)
    return (
      maybeErrorMessage ?? 'Notion integration error. Please contact support.'
    )
  }
}

async function enableNotion(): Promise<void> {
  try {
    validation.value.reset()
    wait.start('Enabling')
    const credentials = {
      notion_database_id: parseDatabaseUrl(database.value || ''),
      notion_integration_token: token.value || '',
    }
    await backend.saveNotionCredentials(credentials)
    emit('updateEnabled', true)
  } catch (error) {
    validation.value.showMessage('notion_token', getErrorMessage(error))
  } finally {
    wait.end('Enabling')
  }
}

function disableNotion(): void {
  popupDisable.value?.show()
}

async function disableIntegration(): Promise<void> {
  await backend.removeNotionCredentials()
}

return (_ctx: any,_cache: any) => {
  const _component_v_wait = _resolveComponent("v-wait")!
  const _directive_wait = _resolveDirective("wait")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[13] || (_cache[13] = _createElementVNode("p", { class: "export-content__hint export-content__step-hint" }, [
        _createElementVNode("span", { class: "export-content__step-info" }, "Step 1"),
        _createTextVNode(": Create a new "),
        _createElementVNode("a", {
          class: "export-content__link",
          target: "_blank",
          href: "https://www.notion.so/my-integrations"
        }, "Notion integration"),
        _createTextVNode(", then copy and paste the Internal Integration Token below. ")
      ], -1)),
      _createElementVNode("form", {
        role: "form",
        class: "form export-content__form_notion",
        novalidate: "",
        onSubmit: _withModifiers(checkForm, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _cache[3] || (_cache[3] = _createElementVNode("label", {
              class: "export-content__label",
              for: "token"
            }, "API key", -1)),
            _createVNode(FormError, {
              errors: validation.value.errors,
              field: "notion_token"
            }, null, 8, ["errors"])
          ]),
          _createVNode(InputWithContextMenu, {
            id: "notion_integration_token",
            ref: "input_token",
            modelValue: token.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((token).value = $event)),
            name: "token",
            rows: "1",
            type: "text",
            class: _normalizeClass(["export-content__input", validation.value.stateClass('notion_token')]),
            placeholder: "Paste Notion API key",
            disabled: __props.isNotionEnabled
          }, null, 8, ["modelValue", "disabled", "class"])
        ]),
        _createElementVNode("p", {
          class: _normalizeClass(["export-content__hint export-content__step-hint", { 'export-content__step-disabled': !token.value }])
        }, [...(_cache[4] || (_cache[4] = [
          _createElementVNode("span", { class: "export-content__step-info" }, "Step 2", -1),
          _createTextVNode(": Open our pre-configured ", -1),
          _createElementVNode("a", {
            class: "export-content__link",
            target: "_blank",
            href: "https://shortform.notion.site/shortform/fcfff9e5e04e47929f2dbb925ecde80d"
          }, "Shortform database template", -1),
          _createTextVNode(" and click on ", -1),
          _createElementVNode("strong", null, "\"Duplicate\"", -1),
          _createTextVNode(" on the upper right corner to add it to your Notion workspace. ", -1)
        ]))], 2),
        _createElementVNode("p", {
          class: _normalizeClass(["export-content__hint export-content__step-hint", { 'export-content__step-disabled': !token.value }])
        }, [...(_cache[5] || (_cache[5] = [
          _createElementVNode("span", { class: "export-content__step-info" }, "Step 3", -1),
          _createTextVNode(": On the newly created database, click the ", -1),
          _createElementVNode("strong", null, "three-dot icon (...)", -1),
          _createTextVNode(" on the upper right, and click on ", -1),
          _createElementVNode("strong", null, "\"Add connections\"", -1),
          _createTextVNode(". Find the name of the integration you created, click it, then press ", -1),
          _createElementVNode("strong", null, "\"Confirm\"", -1),
          _createTextVNode(". ", -1)
        ]))], 2),
        _createElementVNode("p", {
          class: _normalizeClass(["export-content__hint export-content__step-hint", { 'export-content__step-disabled': !token.value }])
        }, [...(_cache[6] || (_cache[6] = [
          _createElementVNode("span", { class: "export-content__step-info" }, "Step 4", -1),
          _createTextVNode(": Copy and paste your new database's URL below! ", -1)
        ]))], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["export-content__form--section", { 'export-content__step-disabled': !token.value }])
        }, [
          _createElementVNode("div", _hoisted_5, [
            _cache[7] || (_cache[7] = _createElementVNode("label", {
              class: "export-content__label",
              for: "database_url"
            }, "Notion link", -1)),
            _createVNode(FormError, {
              errors: validation.value.errors,
              field: "notion_token"
            }, null, 8, ["errors"])
          ]),
          _createVNode(InputWithContextMenu, {
            id: "notion_database_id",
            ref: "input_url",
            modelValue: database.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((database).value = $event)),
            name: "database",
            rows: "1",
            type: "text",
            class: _normalizeClass(["export-content__input", validation.value.stateClass('notion_token')]),
            placeholder: "https://",
            disabled: __props.isNotionEnabled || !token.value
          }, null, 8, ["modelValue", "disabled", "class"])
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["export-content__form--section", { 'export-content__step-disabled': !token.value || !database.value }])
        }, [
          _cache[12] || (_cache[12] = _createElementVNode("div", { class: "export-content__wrapper-info export-content__wrapper export-content__step-hint" }, [
            _createElementVNode("span", { class: "export-content__wrapper--text" }, " This integration automatically exports your highlights to the selected Notion database. If you have used the integration before and you would like to avoid duplicates, you may want to create a new database. ")
          ], -1)),
          _createElementVNode("div", _hoisted_6, [
            (!__props.isNotionEnabled)
              ? _withDirectives((_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "submit",
                  disabled: !token.value || !database.value,
                  class: "btns"
                }, [
                  _createVNode(_component_v_wait, { for: "Enabling" }, {
                    waiting: _withCtx(() => [
                      _cache[8] || (_cache[8] = _createTextVNode(" Enabling ", -1)),
                      _createVNode(ButtonSpinner)
                    ]),
                    default: _withCtx(() => [
                      _cache[9] || (_cache[9] = _createTextVNode(" Connect and Export ", -1))
                    ]),
                    _: 1
                  })
                ], 8, _hoisted_7)), [
                  [_directive_wait, 'Enabling', "disabled"]
                ])
              : _createCommentVNode("", true),
            (__props.isNotionEnabled)
              ? _withDirectives((_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "submit",
                  class: "btns export-content__button-cancel",
                  disabled: !token.value || !database.value
                }, [
                  _createVNode(_component_v_wait, { for: "Disconnecting" }, {
                    waiting: _withCtx(() => [
                      _cache[10] || (_cache[10] = _createTextVNode(" Disconnecting ", -1)),
                      _createVNode(ButtonSpinner)
                    ]),
                    default: _withCtx(() => [
                      _cache[11] || (_cache[11] = _createTextVNode(" Disconnect ", -1))
                    ]),
                    _: 1
                  })
                ], 8, _hoisted_8)), [
                  [_directive_wait, 'Disconnecting', "disabled"]
                ])
              : _createCommentVNode("", true)
          ])
        ], 2),
        _createVNode(DisablePopup, {
          ref_key: "popupDisable",
          ref: popupDisable,
          "integration-name": "Notion",
          disableIntegration: disableIntegration,
          afterDisableCallback: refresh,
          onUpdateEnabled: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('updateEnabled', false)))
        }, null, 512)
      ], 32)
    ])
  ]))
}
}

})